// Feature: address-geolocation, UX helper
//
// parseLegacyAddress — pure heuristic parser.
//
// Given a comma-separated legacy free-text address (the pre-P2 shape of
// `Building.address` and `Contact.address`), produce a best-effort
// `Partial<Address>` so an <AddressForm> can render pre-filled fields
// instead of forcing the user to retype every part.
//
// The parser is I/O-free, deterministic, and never throws. It does NOT
// geocode. It does NOT persist. It ONLY reshapes the string so a human
// can eyeball and correct the auto-mapped fields before saving.
//
// Assumptions (kept conservative — the user always gets the final word):
//   - UK-centric address layout (the only market the app targets today).
//   - Comma-delimited parts in the canonical postal order:
//     `line1[, line2][, locality][, town][, county], postcode[, country]`.
//   - Recognisable UK postcodes at the tail get lifted into `postcode`.
//   - "United Kingdom" / "UK" / "GB" / "Great Britain" at the tail →
//     `countryCode = "GB"`.
//   - Any remaining trailing part that looks like a county (`*shire`
//     suffix or a known-county name) is lifted into `county`.
//   - The last remaining part is treated as `town`.
//   - The second-to-last remaining part (if any) is treated as `line2`.
//   - Everything before that joins back into `line1` with ", ".
//
// The parser never invents a country code. If the legacy string carries
// no country hint, `countryCode` is left undefined — the AddressForm
// placeholder ("GB") still hints at the expected value without lying
// about what was stored.

import type { Address } from "../models";

/**
 * Well-known UK postcode pattern. Anchored so we only match when the
 * entire trimmed part is a postcode — we don't want "10 St George's
 * Road, EC1A 1AB extension" to lose the extension.
 */
const UK_POSTCODE = /^([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})$/i;

/**
 * ISO 3166-1 alpha-2 pattern (any two letters). Used for the rare case
 * where the legacy string trails with a bare country code.
 */
const ISO_ALPHA2 = /^[A-Z]{2}$/i;

/**
 * Country-name synonyms that all normalise to `GB`. Kept short and
 * limited to the app's actual market — extending this list is cheap
 * when new markets come online.
 */
const UK_COUNTRY_NAMES = new Set([
  "united kingdom",
  "uk",
  "u.k.",
  "great britain",
  "gb",
  "england",
  "scotland",
  "wales",
  "northern ireland",
]);

/**
 * A curated set of UK county names that don't end in "shire".
 * Combined with the "-shire" suffix rule this covers the common cases
 * without needing a full postcode-district lookup table.
 */
const KNOWN_UK_COUNTIES = new Set([
  "avon",
  "bedfordshire",
  "berkshire",
  "buckinghamshire",
  "cambridgeshire",
  "cheshire",
  "cornwall",
  "county durham",
  "cumbria",
  "derbyshire",
  "devon",
  "dorset",
  "durham",
  "east sussex",
  "east yorkshire",
  "essex",
  "gloucestershire",
  "greater london",
  "greater manchester",
  "hampshire",
  "herefordshire",
  "hertfordshire",
  "isle of wight",
  "kent",
  "lancashire",
  "leicestershire",
  "lincolnshire",
  "merseyside",
  "middlesex",
  "norfolk",
  "north yorkshire",
  "northamptonshire",
  "northumberland",
  "nottinghamshire",
  "oxfordshire",
  "rutland",
  "shropshire",
  "somerset",
  "south yorkshire",
  "staffordshire",
  "suffolk",
  "surrey",
  "sussex",
  "tyne and wear",
  "warwickshire",
  "west midlands",
  "west sussex",
  "west yorkshire",
  "wiltshire",
  "worcestershire",
  "yorkshire",
]);

function isLikelyUkCounty(part: string): boolean {
  const s = part.trim().toLowerCase();
  if (s.length === 0) return false;
  if (/shire$/.test(s)) return true;
  return KNOWN_UK_COUNTIES.has(s);
}

function isLikelyUkPostcode(part: string): boolean {
  return UK_POSTCODE.test(part.trim());
}

function normaliseCountry(part: string): string | undefined {
  const s = part.trim();
  if (s.length === 0) return undefined;
  if (UK_COUNTRY_NAMES.has(s.toLowerCase())) return "GB";
  if (ISO_ALPHA2.test(s)) return s.toUpperCase();
  return undefined;
}

function normalisePostcode(part: string): string | undefined {
  const s = part.trim();
  const m = UK_POSTCODE.exec(s);
  if (!m) return undefined;
  // Canonicalise to "OUTWARD INWARD" with a single space.
  return `${m[1].toUpperCase()} ${m[2].toUpperCase()}`;
}

/**
 * Parse a legacy free-text address string into a best-effort
 * `Partial<Address>`. Returns an empty object when the input is empty
 * or the parts count / shape offers no useful mapping.
 *
 * Deterministic: the same input always produces the same output.
 */
export function parseLegacyAddress(legacy: string): Partial<Address> {
  if (typeof legacy !== "string") return {};
  const trimmed = legacy.trim();
  if (trimmed.length === 0) return {};

  // Split, trim, and drop empty segments (handles the ", ," double-comma
  // shape seen in re-formatted-then-partial legacy strings).
  const parts = trimmed
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  if (parts.length === 0) return {};

  const result: Partial<Address> = {};

  // Tail-first: peel country if the last part matches.
  const maybeCountry = normaliseCountry(parts[parts.length - 1] ?? "");
  if (maybeCountry) {
    result.countryCode = maybeCountry;
    parts.pop();
  }

  // Then postcode.
  const maybePostcode = normalisePostcode(parts[parts.length - 1] ?? "");
  if (maybePostcode) {
    result.postcode = maybePostcode;
    parts.pop();
  }

  if (parts.length === 0) return result;

  // Then county (only when we still have at least 2 parts left — one
  // remaining part must be the town, so we don't strand a county with
  // nothing else).
  if (parts.length >= 2 && isLikelyUkCounty(parts[parts.length - 1]!)) {
    result.county = parts.pop()!;
  }

  if (parts.length === 0) return result;

  // Special-case a single remaining part: it's overwhelmingly likely to
  // be the street line (users typing a stub address type the street,
  // not the town). Mapping to `town` would leave the required `line1`
  // empty and force a retype — the opposite of the goal.
  if (parts.length === 1) {
    result.addressLine1 = parts[0]!;
    return result;
  }

  // Otherwise the last remaining part is the town.
  result.town = parts.pop()!;

  // If two or more remain, the last is line2, everything before is line1.
  if (parts.length >= 2) {
    result.addressLine2 = parts.pop()!;
  }

  // Everything left forms line1. Multi-part line1 is joined with ", ".
  result.addressLine1 = parts.join(", ");

  return result;
}
